import { createSelector } from 'reselect';
import { getActiveTab } from '../selectors';
import { some } from 'lodash';
import { getSelectedOrganization } from '../SingleChartView/Trend/TrendChart/selectors';
import { chartViews, nameplateFrequency } from './constants';
import { BEARING_TYPES, FFT_UNITS } from 'sharedComponents/Chart/constants';
import { createErrorDataSelector } from 'selectors/selectorUtils';
import { apiCallStatus } from 'helpers/constants';

const getFFTData = ({ operationalParameters }) => {
    return operationalParameters?.FFTV2?.data;
};

const getFFT2DData = ({ operationalParameters }) => {
    return operationalParameters?.FFT2DV2?.data;
};

const hasRawData = ({ operationalParameters }) => {
    if (operationalParameters?.fftV2ChartView === chartViews.FFT2D) {
        return operationalParameters?.FFT2DV2?.data?.length > 0 && some(operationalParameters?.FFT2DV2?.data, (fft) => {
            return fft?.dataValues?.length > 0;
        });
    }
    return operationalParameters?.FFTV2?.data?.length > 0 && some(operationalParameters?.FFTV2?.data, (fft) => {
        return fft?.dataValues?.length > 0;
    });
};

const getFFTDataLoadingStatus = ({ operationalParameters }) => {
    return operationalParameters?.FFTV2?.loadingStatus;
};

const getFFTTimestampList = ({ operationalParameters }) => {
    return operationalParameters?.fftTimestampList;
};

const getTrendKPIsList = ({ operationalParameters }) => {
    return operationalParameters?.availableKPIs?.trend?.data;
};

const getFFT2DDataLoadingStatus = ({ operationalParameters }) => {
    return operationalParameters?.FFT2DV2?.loadingStatus;
};

const getSensortypeID = ({ detailedInformation }) => {
    return detailedInformation?.assetDetails?.data?.details?.sensorTypeID;
};

const getSensorType = ({ detailedInformation }) => {
    return detailedInformation?.assetDetails?.data?.details?.sensorType;
};

const getAssetType = ({ detailedInformation }) => {
    return detailedInformation?.assetDetails?.data?.details?.assetType?.assetType;
};

const getSpeed = ({ detailedInformation }) => {
    const nameplateData = detailedInformation.assetDetails?.data?.sections.find(
        (section) => section.sectionKey === 'NameplateData')?.data;
    const nameplateSpeed = nameplateData?.find(
        (entry) => entry.propertyKey === 'Nameplate_Speed'
    )?.propertyValue;
    return nameplateFrequency && nameplateSpeed && nameplateSpeed / nameplateFrequency;
};

const getDateSelection = ({ overview }) => {
    return overview.dateSelection;
};

export const getSelectedTimestamps = ({ operationalParameters }) => {
    return operationalParameters?.fftV2SelectedTimestamps || {};
};

export const getSelected2DTimestamp = ({ operationalParameters }) => {
    return operationalParameters?.fft2DV2SelectedTimestamp || {};
};

const getAssetDetails = ({ powertrainAssetDetails }) => {
    return powertrainAssetDetails.selectedAsset;
};

const getSelectedLegends = ({ operationalParameters }) => {
    return operationalParameters?.fftV2SelectedLegends || {};
};

const getSelected2DLegends = ({ operationalParameters }) => {
    return operationalParameters?.fft2DV2SelectedLegends || {};
};

const getSelectedSpectrumRange = ({ operationalParameters }) => {
    return operationalParameters?.fftV2SelectedSpectrumRange;
};

export const getChartView = ({ operationalParameters }) => {
    return operationalParameters?.fftV2ChartView || chartViews.FFT2D;
};

const getSelectedXAxisUnit = ({ operationalParameters }) => {
    return operationalParameters?.fftV2SelectedXAxisUnit || FFT_UNITS.HZ;
};

const getSelectedBearingSwitch = ({ operationalParameters }) => {
    return operationalParameters?.bearingFaultSwitch;
};

const getSelectedBearingType = ({ operationalParameters }) => {
    return operationalParameters?.bearingFaultType || BEARING_TYPES.DE;
};

const getSelectedBearingFrequencies = ({ operationalParameters }) => {
    return operationalParameters?.bearingFrequencies;
};

const getSelectedSpeedHarmonicsSwitch = ({ operationalParameters }) => {
    return operationalParameters?.speedHarmonicsSwitch;
};

const getSelectedEchartView = ({ operationalParameters }) => {
    return operationalParameters?.fftV2EchartView;
};

const getSelectedEchartLegend = ({ operationalParameters }) => {
    return operationalParameters?.fftV2EchartLegend;
};

const getSelectedEchartBandSettings = ({ operationalParameters }) => {
    return operationalParameters?.fftV2EchartBandSettings;
};

const getFrequencyRange = ({ operationalParameters }) => {
    return operationalParameters?.frequencyRange;
};

const getRawFFTErrorData = ({ operationalParameters }) => {
    if (operationalParameters?.FFTV2?.loadingStatus === apiCallStatus.ERROR) {
        return {
            loadingStatus: operationalParameters.FFTV2.loadingStatus,
            error: operationalParameters.FFTV2.error,
            errorCode: operationalParameters.FFTV2.error?.statusCode,
        };
    }
    if (operationalParameters?.FFT2DV2?.loadingStatus === apiCallStatus.ERROR) {
        return {
            loadingStatus: operationalParameters.FFT2DV2.loadingStatus,
            error: operationalParameters.FFT2DV2.error,
            errorCode: operationalParameters.FFT2DV2.error?.statusCode,
        };
    }
    return { loadingStatus: operationalParameters?.FFTV2?.loadingStatus };
};

const getFFTErrorData = createErrorDataSelector(getRawFFTErrorData);

const getRawDownloadRawDataErrorData = ({ operationalParameters }) => {
    if (operationalParameters?.downloadRawDataLoadingStatus === apiCallStatus.ERROR) {
        return {
            loadingStatus: operationalParameters.downloadRawDataLoadingStatus,
            error: operationalParameters.downloadRawDataError,
            errorCode: operationalParameters.downloadRawDataError?.statusCode,
        };
    }
    return { loadingStatus: operationalParameters?.downloadRawDataLoadingStatus };
};

const getDownloadRawDataErrorData = createErrorDataSelector(getRawDownloadRawDataErrorData);

export const getModel = createSelector([
    getActiveTab,
    getFFTData,
    getFFT2DData,
    getFFTDataLoadingStatus,
    getFFT2DDataLoadingStatus,
    hasRawData,
    getSpeed,
    getSensortypeID,
    getDateSelection,
    getFFTTimestampList,
    getTrendKPIsList,
    getSelectedTimestamps,
    getSelectedOrganization,
    getAssetDetails,
    getSelected2DTimestamp,
    getSelectedLegends,
    getSelected2DLegends,
    getSelectedSpectrumRange,
    getChartView,
    getSelectedXAxisUnit,
    getSensorType,
    getAssetType,
    getSelectedBearingSwitch,
    getSelectedBearingType,
    getSelectedBearingFrequencies,
    getSelectedSpeedHarmonicsSwitch,
    getSelectedEchartView,
    getSelectedEchartLegend,
    getSelectedEchartBandSettings,
    getFrequencyRange,
    getFFTErrorData,
    getDownloadRawDataErrorData,
], (
    activeTab,
    fftData,
    fft2DData,
    fftDataLoadingStatus,
    fft2DDataLoadingStatus,
    hasRawData,
    speed,
    sensorTypeID,
    dateSelection,
    fftTimestampList,
    trendKPIsList,
    selectedTimestamps,
    selectedOrganization,
    selectedAsset,
    selected2DTimestamp,
    selectedLegends,
    selected2DLegends,
    selectedSpectrumRange,
    selectedChartView,
    selectedXAxisUnit,
    sensorType,
    assetType,
    bearingFaultSwitch,
    bearingFaultType,
    bearingFrequencies,
    speedHarmonicsSwitch,
    selectedEchartView,
    selectedEchartLegend,
    selectedEchartBandSettings,
    frequencyRange,
    errorData,
    downloadRawDataErrorData,
) => {
    return {
        activeTab,
        fftData,
        fft2DData,
        fftDataLoadingStatus,
        fft2DDataLoadingStatus,
        hasRawData,
        speed,
        sensorTypeID,
        dateSelection,
        fftTimestampList,
        trendKPIsList,
        selectedTimestamps,
        selectedOrganization,
        selectedAsset,
        selected2DTimestamp,
        selectedLegends,
        selected2DLegends,
        selectedSpectrumRange,
        selectedChartView,
        selectedXAxisUnit,
        sensorType,
        assetType,
        bearingFaultSwitch,
        bearingFaultType,
        bearingFrequencies,
        speedHarmonicsSwitch,
        selectedEchartView,
        selectedEchartLegend,
        selectedEchartBandSettings,
        frequencyRange,
        errorData,
        downloadRawDataErrorData,
    };
});
